import React from 'react'
import * as yup from 'yup'
import { toast } from 'react-toastify'
import { SubmitHandler, useForm } from 'react-hook-form'
import { useApi } from '~/store/store'
import { HfTextInput } from '~/components'
import { mutualTheme } from '~/utils/theme'
import { CheckIcon, CloseIcon } from '~/assets/icons'
import { yupResolver } from '@hookform/resolvers/yup'
import { useAuthAccount } from '~/store/auth/useAuthAccount'
import { Box, Button, CircularProgress, Typography, useMediaQuery } from '@material-ui/core'
import { CashedDataPayload, AccountPendenciesCachedData, MODAL_TYPE } from '../..'
import { OnboardingRegisterDataActions, trackEvent } from '~/service/analytics'

export interface SocialNetworkInfoModal {
  toggleModal: () => void,
  socialNetwork?: SocialNetwork,
  cachedData: AccountPendenciesCachedData,
  setOpenedModalType: (modalType: MODAL_TYPE) => void,
  modifyCachedData: (key: keyof AccountPendenciesCachedData, payload: CashedDataPayload) => void
}

interface SocialNetwork {
  facebook: string,
  instagram: string,
  linkedIn: string,
  companyWebSite: string
}

const SOCIAL_NETWORK_DEFAULT_VALUES = {
  facebook: '',
  instagram: '',
  linkedIn: '',
  companyWebSite: ''
}

const validationSchema = yup.object().shape({
  facebook: yup.string(),
  instagram: yup.string(),
  linkedIn: yup.string(),
  companyWebSite: yup.string()
})

export const SocialNetworkInfoModal: React.FC<SocialNetworkInfoModal> = (props) => {
  const { modifyCachedData, cachedData, toggleModal } = props
  const { socialMediaObject: socialNetwork } = cachedData
  const { _id: accountId } = useAuthAccount()
  const [pjSetSocialMedia, pjSetSocialMediaState] = useApi('pjSetSocialMedia')
  const { loading: pjSetSocialMediaStateLoading } = pjSetSocialMediaState

  const matches = useMediaQuery(mutualTheme.breakpoints.down('md'))

  const { handleSubmit, control, formState: { errors } } = useForm<SocialNetwork>({
    defaultValues: socialNetwork ? socialNetwork : SOCIAL_NETWORK_DEFAULT_VALUES,
    resolver: yupResolver(validationSchema)
  })

  const onSubmit: SubmitHandler<SocialNetwork> = async (formData) => {
    const { facebook, instagram, linkedIn, companyWebSite } = formData

    const response = await pjSetSocialMedia({
      accountId,
      socialMedia: {
        instagram,
        facebook,
        linkedIn,
        companyWebSite
      }
    })

    if (response.ok) {
      modifyCachedData('socialMediaObject', formData)
      trackEvent(OnboardingRegisterDataActions.FULL_SOCIAL_MEDIA)
      toggleModal()
      return
    }

    toast.error('Erro ao modificar redes sociais.', {
      position: toast.POSITION.TOP_RIGHT,
      autoClose: 3000,
      closeButton: false
    })
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', textAlign: 'center', marginBottom: 4 }}>
          <Typography sx={{ fontWeight: 'fontWeightBold' }} variant={matches ? 'airbnbH3' : 'airbnbH2'}>
            Redes Sociais
          </Typography>
          <Typography variant={matches ? 'airbnbBody2' : 'airbnbBody1'} sx={{ color: 'textGray1.main' }}>
            (Opcional) Mostre a presença da sua empresa nas redes sociais.
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, gap: 3 }}>
          <HfTextInput
            shrink
            label='Facebook'
            name='facebook'
            control={control}
            error={errors.facebook?.message}
            errorMessage={errors.facebook?.message}
            placeholder='Digite seu Facebook'
          />
          <HfTextInput
            shrink
            label='Instagram'
            name='instagram'
            control={control}
            error={errors.instagram?.message}
            errorMessage={errors.instagram?.message}
            placeholder='Digite o seu endereço do Instagram'
          />
          <HfTextInput
            shrink
            label='Linkedin'
            name='linkedIn'
            control={control}
            error={errors.linkedIn?.message}
            errorMessage={errors.linkedIn?.message}
            placeholder='Digite o seu endereço do Linkedin'
          />
          <HfTextInput
            shrink
            label='Site'
            name='companyWebSite'
            control={control}
            error={errors.companyWebSite?.message}
            errorMessage={errors.companyWebSite?.message}
            placeholder='Digite o endereço do seu site'
          />
        </Box>
        <Box sx={{
          display: 'flex',
          flexDirection: [
            'column-reverse',
            'column-reverse',
            'row',
            'row',
            'row'
          ],
          justifyContent: 'space-between',
          marginTop: 3
        }}>
          <Button
            color='info'
            size='large'
            variant='contained'
            onClick={toggleModal}
            startIcon={<CloseIcon htmlColor={mutualTheme.palette.secondary.main} />}
            sx={{
              marginTop: [2, 2, 0, 0, 0],
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold',
              color: 'secondary.main',
              ':hover': {
                bgcolor: 'rgba(54,141,247,0.2)'
              }
            }}
          >
            Cancelar
          </Button>

          <Button
            type='submit'
            variant='contained'
            color='primary'
            size='large'
            disabled={pjSetSocialMediaStateLoading}
            startIcon={pjSetSocialMediaStateLoading
              ? <CircularProgress color='inherit' size={20} />
              : <CheckIcon htmlColor={mutualTheme.palette.common.white} />
            }
            sx={{
              height: 56,
              fontFamily: 'Airbnb',
              fontWeight: 'fontWeightBold'
            }}
          >
            Salvar
          </Button>
        </Box>
      </Box>
    </form>
  )
}
